import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiFetch, apiJson, clearApiAccessToken } from './apiClient';
import { normalizeRole } from './utils/userLocale';
import { setSessionBranding } from './utils/tenantBranding';

const AuthContext = createContext(null);
const SESSION_LOGIN_MARKER = 'cbonline.activeLoginSession';
const LAST_ACTIVE_AT_KEY = 'cbonline.lastActiveAt';
const INACTIVITY_MS = 30 * 60 * 1000;

function setSessionMarker() { try { window.sessionStorage.setItem(SESSION_LOGIN_MARKER, '1'); } catch {} }
function clearSessionMarker() { try { window.sessionStorage.removeItem(SESSION_LOGIN_MARKER); } catch {} }
function setLastActiveNow() {
  try {
    window.localStorage.setItem(LAST_ACTIVE_AT_KEY, String(Date.now()));
  } catch {}
}
function clearLastActive() {
  try {
    window.localStorage.removeItem(LAST_ACTIVE_AT_KEY);
  } catch {}
}
function getLastActive() {
  try {
    const raw = window.localStorage.getItem(LAST_ACTIVE_AT_KEY);
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function mapUser(u) {
  if (!u) return null;
  const mapped = {
    ...u,
    role: normalizeRole(u.role),
    tenant: u.tenant ?? null,
    subscription: u.subscription ?? null,
    modules: Array.isArray(u.modules) ? u.modules : null,
    preferences: u.preferences && typeof u.preferences === 'object' ? u.preferences : null,
    branch: u.branch ?? null,
  };
  setSessionBranding(mapped);
  return mapped;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    setAuthLoading(true);
    const now = Date.now();
    const lastActive = getLastActive();
    if (lastActive > 0 && now - lastActive > INACTIVITY_MS) {
      try {
        await apiFetch('/logout', { method: 'POST', body: '{}' });
      } catch {
        /* network */
      }
      clearApiAccessToken();
      clearSessionMarker();
      clearLastActive();
      setUser(null);
      setSessionBranding(null);
      setAuthLoading(false);
      return;
    }
    try {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 6000);
      let data;
      try {
        data = await apiJson('/session', { signal: controller.signal });
      } finally {
        window.clearTimeout(timeoutId);
      }
      if (data?.user) {
        setSessionMarker();
        setLastActiveNow();
        setUser((prev) => ({
          ...mapUser(data.user),
          sessionStartedAt: prev?.sessionStartedAt ?? new Date().toISOString(),
        }));
      } else {
        clearApiAccessToken();
        clearSessionMarker();
        clearLastActive();
        setUser(null);
        setSessionBranding(null);
      }
    } catch {
      // If session probe fails (network/server), keep user logged out safely.
      clearApiAccessToken();
      clearSessionMarker();
      clearLastActive();
      setUser(null);
      setSessionBranding(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = useCallback((userFromApi) => {
    setSessionMarker();
    setLastActiveNow();
    setUser({ ...mapUser(userFromApi), sessionStartedAt: new Date().toISOString() });
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/logout', { method: 'POST', body: '{}' });
    } catch {
      /* network */
    }
    clearApiAccessToken();
    clearSessionMarker();
    clearLastActive();
    setUser(null);
    setSessionBranding(null);
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    let loggingOut = false;
    let lastWrite = 0;
    const throttledTouch = () => {
      const now = Date.now();
      if (now - lastWrite < 15000) return;
      lastWrite = now;
      setLastActiveNow();
    };

    const enforceInactivityLogout = async () => {
      if (loggingOut) return;
      const lastActive = getLastActive();
      if (!lastActive) return;
      if (Date.now() - lastActive <= INACTIVITY_MS) return;
      loggingOut = true;
      await logout();
    };

    const onActivity = () => throttledTouch();
    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        void enforceInactivityLogout();
      }
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));
    window.addEventListener('focus', onVisibilityOrFocus);
    document.addEventListener('visibilitychange', onVisibilityOrFocus);

    const interval = window.setInterval(() => {
      void enforceInactivityLogout();
    }, 60000);

    return () => {
      window.clearInterval(interval);
      events.forEach((evt) => window.removeEventListener(evt, onActivity));
      window.removeEventListener('focus', onVisibilityOrFocus);
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
    };
  }, [user, logout]);

  const updateProfile = useCallback(async (profilePhoto) => {
    const data = await apiJson('/me', {
      method: 'PATCH',
      body: JSON.stringify({ profilePhoto }),
    });
    setUser((prev) => ({
      ...prev,
      ...mapUser(data.user),
      sessionStartedAt: prev?.sessionStartedAt || new Date().toISOString(),
    }));
  }, []);

  const value = {
    user,
    authLoading,
    refreshMe,
    login,
    logout,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
